import { ClockCircleOutlined } from "@ant-design/icons";
import { useFormat } from "@ap/i18n";
import { useTranslation } from "react-i18next";
import { useNow } from "../hooks";
import { statusPalette } from "../theme";

export type SlaState = "ok" | "due_soon" | "breached";

/** Classifies the time left: breached (< 0), due soon (< 25% of 4h or < 30 min), otherwise ok. */
export function slaState(dueAt: string | number | Date, now: number, soonMs = 30 * 60_000): SlaState {
  const left = new Date(dueAt).getTime() - now;
  if (left < 0) return "breached";
  if (left < soonMs) return "due_soon";
  return "ok";
}

type Fmt = ReturnType<typeof useFormat>;

/** "1 day 22 hours", "3 hours", "12 minutes" or "less than a minute", in the user's language. */
export function friendlyDuration(ms: number, f: Fmt, t: (k: string) => string): string {
  const unit = (n: number, u: "day" | "hour" | "minute") => f.number(n, { style: "unit", unit: u, unitDisplay: "long" });
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return t("sla.underMinute");
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0) return hours > 0 ? `${unit(days, "day")} ${unit(hours, "hour")}` : unit(days, "day");
  if (hours > 0) return unit(hours, "hour");
  return unit(mins, "minute");
}

/** Live countdown to a deadline, in plain words (exact time in the tooltip). */
export function SlaTimer({ dueAt }: { dueAt: string | null | undefined }) {
  const now = useNow(30_000);
  const { t } = useTranslation("common");
  const f = useFormat();
  if (!dueAt) return <span data-sla="none">{t("sla.none")}</span>;
  const left = new Date(dueAt).getTime() - now;
  const state = slaState(dueAt, now);
  const p = state === "breached" ? statusPalette.error : state === "due_soon" ? statusPalette.warning : statusPalette.neutral;
  const label = state === "breached" ? t("sla.overdue", { time: friendlyDuration(-left, f, t) }) : t("sla.remaining", { time: friendlyDuration(left, f, t) });
  return (
    <span
      data-sla={state}
      title={t("sla.due", { at: f.dateTime(dueAt) })}
      style={{ color: p.color, display: "inline-flex", gap: 4, alignItems: "center", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
    >
      <ClockCircleOutlined aria-hidden />
      <span>{label}</span>
    </span>
  );
}
